import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Spinner, Table, Badge, ButtonGroup } from 'react-bootstrap';
import {
    ResponsiveContainer, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { 
    Calendar, TrendingUp, Users, Car, Clock, Navigation, Compass, RefreshCw, UserCheck, Building,
    ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { getBookingProviderDemand } from '../api';
import SearchableSelect from './SearchableSelect';

const COUNTRIES = [
    { id: '', label: '🌐 Todos' },
    { id: 'CL', label: '🇨🇱 Chile' },
    { id: 'PE', label: '🇵🇪 Perú' },
    { id: 'AR', label: '🇦🇷 Argentina' },
    { id: 'DO', label: '🇩🇴 Rep. Dominicana' },
    { id: 'BR', label: '🇧🇷 Brasil' }
];

const PRESETS = [
    { label: 'Últimos 7 días', days: 7 },
    { label: 'Últimos 30 días', days: 30 },
    { label: 'Este Mes', type: 'current_month' },
    { label: 'Mes Anterior', type: 'previous_month' },
    { label: 'Últimos 90 días', days: 90 }
];

const BookingProviderInsightsView = ({ driverOptions = [], providerOptions = [] }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [country, setCountry] = useState('');
    const [timelineMode, setTimelineMode] = useState('weekly'); // 'weekly' o 'daily'
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [selectedProviderId, setSelectedProviderId] = useState('');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                start_date: startDate,
                end_date: endDate,
                country: country || undefined,
                driver_id: selectedDriverId ? Number(selectedDriverId) : undefined,
                provider_id: selectedProviderId ? Number(selectedProviderId) : undefined
            };
            const res = await getBookingProviderDemand(params);
            setData(res);
        } catch (err) {
            console.error("Error al obtener demanda de proveedores Booking:", err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, country, selectedDriverId, selectedProviderId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePreset = (preset) => {
        const now = new Date();
        if (preset.days) {
            const start = new Date();
            start.setDate(start.getDate() - preset.days);
            setStartDate(start.toISOString().split('T')[0]);
            setEndDate(now.toISOString().split('T')[0]);
        } else if (preset.type === 'current_month') {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            setStartDate(start.toISOString().split('T')[0]);
            setEndDate(now.toISOString().split('T')[0]);
        } else if (preset.type === 'previous_month') {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            setStartDate(start.toISOString().split('T')[0]);
            setEndDate(end.toISOString().split('T')[0]);
        }
    };

    const summary = data?.summary || {};
    const timeline = data?.daily_timeline || [];
    const weeklyTimeline = data?.weekly_timeline || [];
    const vehicleTypes = data?.vehicle_types || [];
    const hourlyDist = data?.hourly_distribution || [];
    const topRoutes = data?.top_routes || [];
    const leadTimes = data?.lead_times || [];

    return (
        <div className="booking-provider-insights animate-fade-in">
            {/* Header & Filtros */}
            <Card className="mb-4 shadow-sm border-0 bg-white">
                <Card.Body className="p-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                        <div>
                            <h5 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                                <span style={{ fontSize: '1.4rem' }}>🚐</span> Demanda & Oportunidad para Proveedores (Booking.com)
                            </h5>
                            <p className="text-muted small mb-0">
                                Métricas de volumen, flotas requeridas y patrones horarios ideales para negociar y planificar con conductores y empresas colaboradoras.
                            </p>
                        </div>
                        <Button 
                            variant="primary" 
                            size="sm" 
                            className="d-flex align-items-center gap-2 fw-semibold px-3"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            <RefreshCw size={14} className={loading ? "spin" : ""} />
                            {loading ? "Actualizando..." : "Actualizar"}
                        </Button>
                    </div>

                    {/* Fila 1: Filtro de Período y Selección de País */}
                    <div className="row g-2 align-items-center pb-2 border-bottom">
                        {/* Selector de Rango y Presets */}
                        <div className="col-12 col-lg-7 d-flex flex-wrap align-items-center gap-2">
                            <span className="small fw-bold text-muted text-uppercase me-1">Período:</span>
                            <ButtonGroup size="sm">
                                {PRESETS.map((p, idx) => (
                                    <Button
                                        key={idx}
                                        variant="outline-secondary"
                                        className="btn-sm text-nowrap py-1 px-2"
                                        onClick={() => handlePreset(p)}
                                    >
                                        {p.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                            <div className="d-inline-flex align-items-center gap-1 ms-lg-2">
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    style={{ width: '130px' }}
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                                <span className="text-muted small">a</span>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    style={{ width: '130px' }}
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Filtro por País */}
                        <div className="col-12 col-lg-5 d-flex flex-wrap align-items-center justify-content-lg-end gap-1">
                            <span className="small fw-bold text-muted text-uppercase me-1">País:</span>
                            <ButtonGroup size="sm">
                                {COUNTRIES.map((c) => (
                                    <Button
                                        key={c.id}
                                        variant={country === c.id ? "primary" : "outline-primary"}
                                        className="py-1 px-2 fw-semibold"
                                        onClick={() => setCountry(c.id)}
                                    >
                                        {c.label}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </div>
                    </div>

                    {/* Fila 2: Filtros Específicos de Conductor y Proveedor */}
                    <div className="row g-2 align-items-center pt-2">
                        <div className="col-12 col-md-5 d-flex align-items-center gap-2">
                            <span className="small fw-bold text-muted text-uppercase d-flex align-items-center gap-1" style={{ minWidth: '85px' }}>
                                <UserCheck size={14} className="text-primary" /> Conductor:
                            </span>
                            <div className="flex-grow-1">
                                <SearchableSelect
                                    options={[{ id: '', full_name: '👤 Todos los conductores' }, ...driverOptions]}
                                    value={selectedDriverId}
                                    onChange={(val) => setSelectedDriverId(val)}
                                    placeholder="👤 Todos los conductores"
                                />
                            </div>
                            {selectedDriverId && (
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    className="py-0 px-2 small" 
                                    onClick={() => setSelectedDriverId('')}
                                    title="Quitar filtro de conductor"
                                >
                                    ✕
                                </Button>
                            )}
                        </div>

                        <div className="col-12 col-md-5 d-flex align-items-center gap-2">
                            <span className="small fw-bold text-muted text-uppercase d-flex align-items-center gap-1" style={{ minWidth: '85px' }}>
                                <Building size={14} className="text-primary" /> Proveedor:
                            </span>
                            <div className="flex-grow-1">
                                <SearchableSelect
                                    options={[{ id: '', name: '🏢 Todos los proveedores' }, ...providerOptions]}
                                    value={selectedProviderId}
                                    onChange={(val) => setSelectedProviderId(val)}
                                    placeholder="🏢 Todos los proveedores"
                                />
                            </div>
                            {selectedProviderId && (
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    className="py-0 px-2 small" 
                                    onClick={() => setSelectedProviderId('')}
                                    title="Quitar filtro de proveedor"
                                >
                                    ✕
                                </Button>
                            )}
                        </div>

                        <div className="col-12 col-md-2 text-md-end">
                            {(selectedDriverId || selectedProviderId) && (
                                <Badge bg="info" className="p-2 text-wrap">
                                    Filtrado activo
                                </Badge>
                            )}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {loading && !data && (
                <div className="text-center p-5 bg-white rounded shadow-sm mb-4">
                    <Spinner animation="border" variant="primary" />
                    <h5 className="mt-3 text-secondary">Analizando operaciones de Booking.com...</h5>
                </div>
            )}

            {data && (
                <>
                    {/* KPI Cards Ejecutivos */}
                    <Row className="g-3 mb-4">
                        <Col xl={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-muted small fw-bold text-uppercase mb-1">Volumen Total Servicios</div>
                                        <h3 className="fw-bold mb-0 text-primary">
                                            {(summary.total_trips || 0).toLocaleString()}
                                        </h3>
                                        <div className="text-muted small mt-1">
                                            En {summary.active_days || 0} días operativos
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-circle bg-primary bg-opacity-10 text-primary">
                                        <TrendingUp size={28} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-muted small fw-bold text-uppercase mb-1">Promedio Diario</div>
                                        <h3 className="fw-bold mb-0 text-success">
                                            {summary.daily_average || 0} <span className="fs-6 text-muted fw-normal">servicios/día</span>
                                        </h3>
                                        <div className="text-muted small mt-1">
                                            Capacidad mínima diaria requerida
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-circle bg-success bg-opacity-10 text-success">
                                        <Car size={28} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-muted small fw-bold text-uppercase mb-1">Tipo de Vehículo Más Solicitado</div>
                                        <h4 className="fw-bold mb-0 text-dark text-truncate" style={{ maxWidth: '180px' }} title={summary.top_vehicle_type}>
                                            {summary.top_vehicle_type || 'N/A'}
                                        </h4>
                                        <div className="text-primary small fw-semibold mt-1">
                                            {summary.top_vehicle_pct || 0}% de la demanda total
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-circle bg-warning bg-opacity-10 text-warning">
                                        <Users size={28} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Body className="p-3 d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="text-muted small fw-bold text-uppercase mb-1">Franja Horaria Peak</div>
                                        <h5 className="fw-bold mb-0 text-danger text-truncate" style={{ maxWidth: '190px' }} title={summary.peak_hour}>
                                            {summary.peak_hour || 'N/A'}
                                        </h5>
                                        <div className="text-muted small mt-1">
                                            Mayor concentración de salidas
                                        </div>
                                    </div>
                                    <div className="p-3 rounded-circle bg-danger bg-opacity-10 text-danger">
                                        <Clock size={28} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Gráficos Principales: Volumen Diario & Distribución por Flota */}
                    <Row className="g-3 mb-4">
                        {/* Evolución Diaria / Semanal */}
                        <Col xl={8} lg={7}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <div>
                                        <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                            <Calendar size={18} className="text-primary" />
                                            {timelineMode === 'weekly' ? 'Evolución Semanal & Crecimiento' : 'Evolución de Viajes por Día'}
                                        </h6>
                                        <small className="text-muted">
                                            {timelineMode === 'weekly' 
                                                ? 'Comparativa semana a semana (Lunes a Domingo) para medir tendencias.' 
                                                : 'Detalle día a día en el rango de fechas seleccionado.'}
                                        </small>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <ButtonGroup size="sm">
                                            <Button 
                                                variant={timelineMode === 'weekly' ? 'primary' : 'outline-primary'}
                                                className="py-1 px-3 fw-semibold"
                                                onClick={() => setTimelineMode('weekly')}
                                            >
                                                📅 Semanal
                                            </Button>
                                            <Button 
                                                variant={timelineMode === 'daily' ? 'primary' : 'outline-primary'}
                                                className="py-1 px-3 fw-semibold"
                                                onClick={() => setTimelineMode('daily')}
                                            >
                                                📆 Diario
                                            </Button>
                                        </ButtonGroup>
                                        <Badge bg="light" text="dark" className="border">
                                            {timelineMode === 'weekly' ? `${weeklyTimeline.length} semanas` : `${timeline.length} días`}
                                        </Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ width: '100%', height: 320 }}>
                                        <ResponsiveContainer>
                                            {timelineMode === 'weekly' ? (
                                                <BarChart data={weeklyTimeline} margin={{ top: 15, right: 20, left: -10, bottom: 25 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis 
                                                        dataKey="label" 
                                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                                        angle={-15}
                                                        textAnchor="end"
                                                        interval={0}
                                                    />
                                                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                                                    <Tooltip 
                                                        content={({ active, payload }) => {
                                                            if (active && payload && payload.length) {
                                                                const p = payload[0].payload;
                                                                return (
                                                                    <div className="bg-white p-2 rounded shadow-sm border small">
                                                                        <div className="fw-bold text-dark">{p.label}</div>
                                                                        <div className="text-primary fw-semibold my-1">
                                                                            📊 {p.trips.toLocaleString()} servicios
                                                                        </div>
                                                                        {p.growth_pct !== null && (
                                                                            <div className={`fw-bold d-flex align-items-center gap-1 ${p.growth_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                                                                                {p.growth_pct >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                                                                                {p.growth_pct >= 0 ? `+${p.growth_pct}% vs semana anterior` : `${p.growth_pct}% vs semana anterior`}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                            return null;
                                                        }}
                                                    />
                                                    <Bar 
                                                        dataKey="trips" 
                                                        fill="#2563eb" 
                                                        radius={[4, 4, 0, 0]} 
                                                        name="Servicios"
                                                    >
                                                        {weeklyTimeline.map((entry, index) => {
                                                            const color = entry.growth_pct === null 
                                                                ? '#2563eb' 
                                                                : entry.growth_pct >= 0 ? '#10b981' : '#f59e0b';
                                                            return <Cell key={`bar-week-${index}`} fill={color} />;
                                                        })}
                                                    </Bar>
                                                </BarChart>
                                            ) : (
                                                <AreaChart data={timeline} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="colorTrips" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4}/>
                                                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0.0}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis 
                                                        dataKey="date" 
                                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                                        tickFormatter={(str) => str ? str.slice(5) : ''}
                                                    />
                                                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                                                    <Tooltip 
                                                        formatter={(val) => [`${val} viajes`, "Servicios"]}
                                                        labelFormatter={(label) => `Fecha: ${label}`}
                                                        contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                                    />
                                                    <Area 
                                                        type="monotone" 
                                                        dataKey="trips" 
                                                        stroke="#2563eb" 
                                                        strokeWidth={2.5}
                                                        fillOpacity={1} 
                                                        fill="url(#colorTrips)" 
                                                        name="Servicios"
                                                    />
                                                </AreaChart>
                                            )}
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Leyenda de variación semanal cuando esté activo el modo Semanal */}
                                    {timelineMode === 'weekly' && weeklyTimeline.length > 1 && (
                                        <div className="mt-2 pt-2 border-top d-flex flex-wrap align-items-center justify-content-between gap-2 small text-muted">
                                            <div className="d-flex align-items-center gap-3">
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }}></span>
                                                    Crecimiento semanal
                                                </span>
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block' }}></span>
                                                    Decrecimiento semanal
                                                </span>
                                            </div>
                                            {(() => {
                                                const lastW = weeklyTimeline[weeklyTimeline.length - 1];
                                                if (lastW && lastW.growth_pct !== null) {
                                                    const isUp = lastW.growth_pct >= 0;
                                                    return (
                                                        <div className={`fw-bold d-flex align-items-center gap-1 ${isUp ? 'text-success' : 'text-danger'}`}>
                                                            Última semana: {isUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                                                            {isUp ? `+${lastW.growth_pct}%` : `${lastW.growth_pct}%`}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Distribución por Tipo de Vehículo */}
                        <Col xl={4} lg={5}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0">
                                    <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                        <Car size={18} className="text-success" />
                                        Distribución por Flota / Vehículo
                                    </h6>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ width: '100%', height: 210 }}>
                                        <ResponsiveContainer>
                                            <PieChart>
                                                <Pie
                                                    data={vehicleTypes}
                                                    dataKey="count"
                                                    nameKey="label"
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={50}
                                                    outerRadius={80}
                                                    paddingAngle={3}
                                                >
                                                    {vehicleTypes.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color || '#3b82f6'} />
                                                    ))}
                                                </Pie>
                                                <Tooltip 
                                                    formatter={(val, name, item) => [`${val} viajes (${item.payload.percentage}%)`, item.payload.label]}
                                                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Lista desglosada */}
                                    <div className="mt-2" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                        {vehicleTypes.map((v, idx) => (
                                            <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom small">
                                                <div className="d-flex align-items-center gap-2 text-truncate">
                                                    <span 
                                                        style={{ 
                                                            width: 10, 
                                                            height: 10, 
                                                            borderRadius: '50%', 
                                                            backgroundColor: v.color || '#3b82f6',
                                                            display: 'inline-block',
                                                            flexShrink: 0
                                                        }} 
                                                    />
                                                    <span className="fw-semibold text-truncate">{v.label}</span>
                                                </div>
                                                <div className="text-nowrap ms-2">
                                                    <span className="fw-bold text-dark">{v.count.toLocaleString()}</span>
                                                    <span className="text-muted ms-1">({v.percentage}%)</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Gráficos Secundarios: Distribución Horaria 24h & Lead Time */}
                    <Row className="g-3 mb-4">
                        {/* Franjas Horarias (00 a 23 hrs) */}
                        <Col xl={7} lg={6}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0">
                                    <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                        <Clock size={18} className="text-danger" />
                                        Curva de Demanda por Hora del Día (00 - 23 hrs)
                                    </h6>
                                    <small className="text-muted">Permite a los transportistas organizar turnos de mañana, tarde y noche.</small>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ width: '100%', height: 260 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={hourlyDist} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                <XAxis 
                                                    dataKey="label" 
                                                    tick={{ fontSize: 10, fill: '#64748b' }} 
                                                    interval={1}
                                                />
                                                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                                                <Tooltip 
                                                    formatter={(val) => [`${val} viajes`, "Demanda"]}
                                                    labelFormatter={(label) => `Hora: ${label}`}
                                                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                />
                                                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Viajes" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Anticipación de la Reserva (Lead Time) */}
                        <Col xl={5} lg={6}>
                            <Card className="border-0 shadow-sm h-100 bg-white">
                                <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0">
                                    <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                        <Compass size={18} className="text-info" />
                                        Anticipación de Reserva (Lead Time)
                                    </h6>
                                    <small className="text-muted">¿Con cuánta antelación reservan los pasajeros de Booking?</small>
                                </Card.Header>
                                <Card.Body className="p-3">
                                    <div style={{ width: '100%', height: 260 }}>
                                        <ResponsiveContainer>
                                            <BarChart 
                                                data={leadTimes} 
                                                layout="vertical"
                                                margin={{ top: 10, right: 20, left: 30, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                                                <YAxis 
                                                    type="category" 
                                                    dataKey="range" 
                                                    tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }}
                                                    width={75}
                                                />
                                                <Tooltip 
                                                    formatter={(val, name, item) => [`${val} reservas (${item.payload.percentage}%)`, item.payload.label]}
                                                    contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                                                />
                                                <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]}>
                                                    {leadTimes.map((entry, index) => (
                                                        <Cell key={`lead-cell-${index}`} fill={entry.color || '#10b981'} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Tabla de Principales Corredores / Rutas */}
                    <Card className="border-0 shadow-sm bg-white mb-4">
                        <Card.Header className="bg-transparent border-0 pt-3 px-3 pb-0 d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                    <Navigation size={18} className="text-primary" />
                                    Top 10 Corredores y Rutas Más Demandadas
                                </h6>
                                <small className="text-muted">Muestra a los proveedores dónde se concentra el trabajo real para dimensionar posicionamiento.</small>
                            </div>
                            <Badge bg="primary" className="fw-semibold">
                                {topRoutes.length} Corredores Clave
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0 mt-2">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 border-top">
                                    <thead className="bg-light">
                                        <tr className="small text-uppercase text-muted">
                                            <th className="ps-3" style={{ width: '40px' }}>#</th>
                                            <th>Origen</th>
                                            <th style={{ width: '40px' }}></th>
                                            <th>Destino</th>
                                            <th className="text-center" style={{ width: '130px' }}>Servicios</th>
                                            <th className="text-center" style={{ width: '130px' }}>% Participación</th>
                                            <th className="text-end pe-3" style={{ width: '140px' }}>Distancia Media</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topRoutes.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="text-center py-4 text-muted">
                                                    No se encontraron rutas para los filtros seleccionados.
                                                </td>
                                            </tr>
                                        ) : (
                                            topRoutes.map((r, idx) => (
                                                <tr key={idx}>
                                                    <td className="ps-3 fw-bold text-muted small">{idx + 1}</td>
                                                    <td className="fw-semibold text-dark">
                                                        {r.origin}
                                                    </td>
                                                    <td className="text-center text-muted">➔</td>
                                                    <td className="fw-semibold text-dark">
                                                        {r.destination}
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge bg="primary" className="px-2 py-1">
                                                            {r.count.toLocaleString()} viajes
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center text-muted fw-semibold">
                                                        {r.percentage}%
                                                    </td>
                                                    <td className="text-end pe-3 text-secondary font-monospace">
                                                        {r.avg_distance_km ? `${r.avg_distance_km} km` : 'S/D'}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </>
            )}
        </div>
    );
};

export default BookingProviderInsightsView;
